import { ComponentProps, ReactNode, useEffect, useState } from 'react';
import * as R from 'remeda';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { getEtag } from '@rossum/api-client';
import { ElisClientError, HttpError } from '@rossum/api-client/errors';
import { Queue } from '@rossum/api-client/queues';
import { SchemaSection } from '@rossum/api-client/schemas';
import { Box, Paper, Stack, Typography } from '@rossum/ui/material';
import { usePatchSchema } from '../../../../business/schema/usePatchSchema';
import { SchemaWithEtag } from '../../../../business/schema/useSchema';
import HiddingButton from '../../../../components/UI/HiddingButton';
import { devHub, helpCenter } from '../../../../constants/values';
import { usePatchQueue } from '../../../../features/queues/hooks/usePatchQueue';
import { LocalizationKeys } from '../../../../i18n';
import { link } from '../../../../lib/formaterValues';
import { throwError } from '../../../../redux/modules/messages/actions';
import AutomationDataTable from './AutomationDataTable';
import AutomationLevels from './AutomationLevels';
import DefaultScoreThreshold, {
  clampThresholdValue,
} from './DefaultScoreThreshold';
import styles from './style.module.sass';

export type ChildrenOrFormattedMessage = {
  children?: ReactNode;
  id?: LocalizationKeys;
  values?: ComponentProps<typeof FormattedMessage>['values'];
};

export const childrenOrFormattedMessage = (props: ChildrenOrFormattedMessage) =>
  props.children || <FormattedMessage id={props.id} values={props.values} />;

const SecondaryText = (props: ChildrenOrFormattedMessage) => (
  <Typography variant="body2" color="text.secondary" paragraph>
    {childrenOrFormattedMessage(props)}
  </Typography>
);

type Props = {
  selectedQueue: Queue;
  parentPath: string;
  schema: SchemaWithEtag;
};

export const AutomationPage = ({
  selectedQueue,
  parentPath,
  schema,
}: Props) => {
  const dispatch = useDispatch();

  const { mutateAsync: patchQueue } = usePatchQueue();
  const { mutateAsync: patchSchema } = usePatchSchema({
    etag: getEtag(schema),
  });

  const originalSchema = schema.content;
  const [updatedSchemaTree, setUpdatedSchemaTree] = useState<SchemaSection[]>(
    () => originalSchema ?? []
  );

  useEffect(() => {
    setUpdatedSchemaTree(originalSchema ?? []);
  }, [originalSchema]);

  const defaultValues = {
    automationLevel: selectedQueue.automationLevel,
    defaultScoreThreshold: selectedQueue.defaultScoreThreshold,
  };

  const { control, handleSubmit, watch } = useForm<
    Pick<Queue, 'defaultScoreThreshold' | 'automationLevel'>
  >({
    mode: 'onSubmit',
    defaultValues,
  });

  const watchValues = watch();

  const queueSettingsChanged = !R.isDeepEqual(watchValues, defaultValues);
  const schemaChanged = !R.isDeepEqual(originalSchema, updatedSchemaTree);

  const noChanges = !queueSettingsChanged && !schemaChanged;

  const onSubmit = (data: Partial<Queue>) => {
    if (queueSettingsChanged) {
      patchQueue({
        queueId: selectedQueue.id,
        payload: {
          ...data,
          automationEnabled: data.automationLevel !== 'never',
        },
      });
    }
    if (schemaChanged) {
      patchSchema({
        id: schema.id,
        payload: { url: schema.url, content: updatedSchemaTree },
      }).catch((error: ElisClientError) =>
        dispatch(
          throwError(
            error instanceof HttpError && error.code === 412
              ? 'schemaUpdateConflict'
              : 'schemaUpdateFailed'
          )
        )
      );
    }
  };

  const clampedThresholdValue = clampThresholdValue(
    watchValues.defaultScoreThreshold
  );

  const intl = useIntl();

  return (
    <Paper sx={{ py: 2 }}>
      <div className={styles['WidthLimit']}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={5} sx={{ marginTop: 4 }}>
            <Box component="section">
              <Typography variant="h6">
                {intl.formatMessage({
                  id: 'containers.settings.automation.documentAutomation.title',
                })}
              </Typography>
              <SecondaryText
                id="containers.settings.automation.documentAutomation.description"
                values={{
                  link: link(devHub('docs/data-capture-automation')),
                }}
              />
              <AutomationLevels control={control} />
            </Box>
            <Box component="section">
              <Typography variant="h6">
                {intl.formatMessage({
                  id: 'containers.settings.automation.fieldAutomation.title',
                })}
              </Typography>
              <SecondaryText
                id="containers.settings.automation.fieldAutomation.description"
                values={{
                  link: link(
                    helpCenter('/docs/fields-with-grey-or-green-ticks/')
                  ),
                }}
              />
              <DefaultScoreThreshold
                control={control}
                defaultValue={defaultValues.defaultScoreThreshold}
              />
            </Box>
            <SecondaryText
              id="containers.settings.automation.fieldAutomation.threshold.description"
              values={{
                link: link(`${parentPath}/fields`),
              }}
            />
            <Box component="section">
              <Typography variant="h6">
                {intl.formatMessage({
                  id: 'containers.settings.automation.automationData.title',
                })}
              </Typography>

              <SecondaryText id="containers.settings.automation.automationData.description_1" />
              <SecondaryText id="containers.settings.automation.automationData.description_2" />

              <ol>
                {(
                  [
                    'containers.settings.automation.automationData.li_1',
                    'containers.settings.automation.automationData.li_2',
                    'containers.settings.automation.automationData.li_3',
                  ] as const
                ).map(key => (
                  <Box
                    component="li"
                    sx={{
                      color: theme => theme.palette.text.secondary,
                    }}
                    key={key}
                  >
                    {intl.formatMessage({ id: key })}
                  </Box>
                ))}
              </ol>
            </Box>
            <AutomationDataTable
              queueId={selectedQueue.id}
              queueThreshold={clampedThresholdValue}
              schema={updatedSchemaTree}
              updateSchema={setUpdatedSchemaTree}
            />
          </Stack>

          <div className={styles['ButtonWrapper']}>
            <HiddingButton
              hideCondition={noChanges}
              messageId="containers.settings.automation.save"
              type="submit"
            />
          </div>
        </form>
      </div>
    </Paper>
  );
};
